import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Observable, Subscription } from 'rxjs';
import { AbstractOnboardingPageComponent } from 'src/app/core/abstracts/components/onboarding-page.abstract';
import { CompanyResultViewTypes } from 'src/app/core/enums/company-result-view.enum';
import { EventTypes } from 'src/app/core/enums/event.enum';
import { IPageContent } from 'src/app/core/interfaces/common/content/page-content.interface';
import { IDashboardChecklistItem } from 'src/app/core/interfaces/common/ui/dashboard-checklist-item.interface';
import { ICompanyInfo } from 'src/app/core/interfaces/onboarding/business-profile/company-Info-model.interface';
import { ITransition } from 'src/app/core/interfaces/onboarding/page-state/transition.interface';
import { CompanySearch } from 'src/app/core/models/business-profile/companySearch.model';
import { PageState } from 'src/app/core/models/page-state/page-state.model';
import { KendoNotificationService } from 'src/app/core/services/common/kendo-notification.service';
import { BusinessProfileService } from 'src/app/core/services/onboarding/business-profile.service';
import { EnrollmentExtendedService } from 'src/app/core/services/onboarding/enrollment-extended.service';
import { PageStateService } from 'src/app/core/services/onboarding/page-state.service';
import { IAppState } from 'src/app/core/store/common/interfaces/app-state.interface';
import { CompanySearchAction } from 'src/app/core/store/onboarding/actions/business-profile.actions';
import { selectRegisteredCompanyName } from 'src/app/core/store/onboarding/reducers+selectors/create-account.selector';
import { BreakpointConstants } from 'src/app/core/ui-helpers/constants/breakpoint-constants';
import { destructSubscriptionArray } from 'src/app/core/ui-helpers/functions/destruct-subscription-array';
import { ContentIDConstants } from './ContentIDConstants';

@Component({
  selector: 'app-disqualified-dashboard',
  templateUrl: './disqualified-dashboard.component.html',
  styleUrls: ['./disqualified-dashboard.component.scss'],
})
export class DisqualifiedDashboardComponent
  extends AbstractOnboardingPageComponent
  implements OnInit, OnDestroy
{
  isTablet: boolean;
  checklistItems: IDashboardChecklistItem[];
  companyNameProp$: Observable<any>;
  subscriptions: Subscription[] = [];
  companyName: string;
  mainHeaderText: string;
  mainSummaryText: string;
  mainSubHeaderText: string;
  mainSubSummaryText: string;

  // disable sidenav states except bp
  homeState = new PageState();
  fpState = new PageState();
  cpState = new PageState();
  disableDocuments = true;
  rescoreState = new PageState();
  modifyBpState = new PageState();
  noMatchState = new PageState();
  disqualifyingAnswerState = new PageState();

  rescoreEligabilityBit: boolean;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private bps: BusinessProfileService,
    private pss: PageStateService,
    private ees: EnrollmentExtendedService,
    private kns: KendoNotificationService,
    private store: Store<IAppState>
  ) {
    super();
    this.isTablet = window.innerWidth < BreakpointConstants.tabletBreakpoint;
  }

  async ngOnInit(): Promise<void> {
    this.subscriptions.push(
      this.route.data.subscribe(async (data) => {
        const { transitions } = data['data'];
        const { contentDict } = data['data'];
        this.rescoreEligabilityBit = data['rescoreEligibility'];
        this.populatePageStates(transitions);
        this.populatePageContent(contentDict);
      })
    );

    this.companyNameProp$ = this.store.select(selectRegisteredCompanyName);
    this.subscriptions.push(
      this.companyNameProp$.subscribe((props) => {
        this.companyName = props;
      })
    );
  }

  ngOnDestroy(): void {
    destructSubscriptionArray(this.subscriptions);
  }

  @HostListener('window:resize')
  onResize() {
    this.isTablet = window.innerWidth < BreakpointConstants.tabletBreakpoint;
  }

  populatePageStates(transitions: ITransition[]): void {
    this.rescoreState = this.pss.getPageStateByEventAndTransitions(
      EventTypes.RESCORE,
      transitions
    );
    this.modifyBpState = this.pss.getPageStateByEventAndTransitions(
      EventTypes.BP_SUMMARY_EDIT,
      transitions
    );
    this.noMatchState = this.pss.getPageStateByEventAndTransitions(
      EventTypes.COMPANY_NOT_FOUND,
      transitions
    );
    this.disqualifyingAnswerState = this.pss.getPageStateByEventAndTransitions(
      EventTypes.INDUSTRY_SOURCE_NOT_QUALIFIED,
      transitions
    );
  }

  populatePageContent(contentDict: Map<number, IPageContent>): void {
    this.mainHeaderText =
      contentDict.get(ContentIDConstants.MainHeader)?.displayText ?? '...';
    this.mainSummaryText =
      contentDict.get(ContentIDConstants.MainSummary)?.displayText ?? '...';
    this.mainSubHeaderText =
      contentDict.get(ContentIDConstants.MainSubHeader)?.displayText ?? '...';
    this.mainSubSummaryText =
      contentDict.get(ContentIDConstants.MainSubSummary)?.displayText ?? '...';
  }

  async rescore() {
    const result = await this.bps.getBestMatch();
    switch (result.response) {
      case CompanyResultViewTypes.BestMatchFound:
        this.rescoreEligabilityBit = false;
        this.dispatchCompanySearch(result);
        if (await this.reactivateBP()) {
          this.router.navigate([this.rescoreState.pageUrl], {
            queryParams: { stateId: this.rescoreState.id },
          });
        }
        break;
      case CompanyResultViewTypes.NoMatchFound:
        this.rescoreEligabilityBit = false;
        this.router.navigate([this.noMatchState.pageUrl], {
          queryParams: { stateId: this.noMatchState.id },
        });
        break;
      case CompanyResultViewTypes.IndustrySourceNotQualified:
      case CompanyResultViewTypes.WrongInvoicingPractices:
      case CompanyResultViewTypes.WrongBusinessIndustry:
      case CompanyResultViewTypes.WrongLendingPractices:
      case CompanyResultViewTypes.WrongCustomers:
      case CompanyResultViewTypes.NotQualified:
      case CompanyResultViewTypes.SoleProprietorship:
        this.rescoreEligabilityBit = false;
        this.router.navigate([this.disqualifyingAnswerState.pageUrl], {
          queryParams: { stateId: this.disqualifyingAnswerState.id },
        });
        break;
      default:
        break;
    }
  }

  async modify() {
    const succeeded = await this.reactivateBP();
    return succeeded;
  }

  private dispatchCompanySearch(companyInfo: ICompanyInfo) {
    const companySearch = new CompanySearch();
    companySearch.registeredCompanyName =
      companyInfo.companySearchResultModel.registeredCompanyName;
    companySearch.address1 = companyInfo.companySearchResultModel.address1;
    companySearch.address2 = companyInfo.companySearchResultModel.address2;
    companySearch.state = companyInfo.companySearchResultModel.state;
    companySearch.city = companyInfo.companySearchResultModel.city;
    companySearch.zipCode = companyInfo.companySearchResultModel.zipCode;
    companySearch.lookupTypeId = companyInfo.lookupTypeId;
    companySearch.lookupId = companyInfo.lookupId;
    companySearch.entityNumber = companyInfo.entityNumber;

    // saving to store
    this.store.dispatch(
      CompanySearchAction({
        companySearch,
      })
    );
  }

  private async reactivateBP(): Promise<boolean> {
    let succeeded = true;
    await Promise.allSettled([
      this.ees.activateBusinessProfile(),
      this.bps.reactivateLead(),
    ]).then((res) =>
      res.forEach((result) => {
        if (result.status === 'fulfilled') {
          if (!result.value.isSuccessful) {
            succeeded = false;
          }
        } else {
          succeeded = false;
        }
      })
    );
    if (!succeeded) {
      this.kns.show('error', 'Something went wrong. Please refresh your page.');
    }
    return succeeded;
  }
}
